import { connectedMessageCaptureHandler } from "./handlers/connectedMessageCaptureHandler";
import { connectedTaskCaptureHandler } from "@/lib/capture/handlers/connectedTaskCaptureHandler";
import { reminderCaptureHandler, reminderPreferencesCaptureHandler } from "@/lib/capture/handlers/reminderCaptureHandler";
import { checkinActionCaptureHandler } from "@/lib/capture/handlers/checkinCaptureHandler";
import { noteActionCaptureHandler, legacyNoteCaptureHandler } from "@/lib/capture/handlers/noteCaptureHandler";
import { goalActionCaptureHandler, goalCaptureHandler } from "@/lib/capture/handlers/goalCaptureHandler";
import { taskActionCaptureHandler, legacyTaskCaptureHandler } from "@/lib/capture/handlers/taskCaptureHandler";
// Universal capture — the dispatcher map.
//
// THIS is the single place to extend capture. To add a module:
//   1. ship its extractor edge fn + confirm modal,
//   2. write a `ModuleCaptureHandler` (see agendaCaptureHandler for the shape),
//   3. add it to the array below.
// The `useModuleCapture` hook body does NOT change.
//
// Lookup is by `proposal.tool`, so order here is irrelevant.

import { personalActionCaptureHandler } from "@/lib/capture/handlers/personalActionCaptureHandler";
import type { ModuleCaptureHandler } from "@/lib/capture/types";
import { agendaCaptureHandler, agendaActionCaptureHandler, createAgendaCaptureHandler } from "@/lib/capture/handlers/agendaCaptureHandler";
import { nutritionCaptureHandler } from "@/lib/capture/handlers/nutritionCaptureHandler";
import { focusCaptureHandler, focusActionCaptureHandler, focusTimerCaptureHandler } from "@/lib/capture/handlers/focusCaptureHandler";
import { sleepCaptureHandler, sleepActionCaptureHandler } from "@/lib/capture/handlers/sleepCaptureHandler";
import { habitCaptureHandler, habitActionCaptureHandler, createHabitCaptureHandler } from "@/lib/capture/handlers/habitCaptureHandler";
import { workoutCaptureHandler } from "@/lib/capture/handlers/workoutCaptureHandler";
import { journalCaptureHandler } from "@/lib/capture/handlers/journalCaptureHandler";
import { financeCaptureHandler, financeActionCaptureHandler } from "@/lib/capture/handlers/financeCaptureHandler";
import { bodyHealthActionCaptureHandler, bodyHealthLegacyCaptureHandler } from "@/lib/capture/handlers/bodyHealthCaptureHandler";
import { emailCaptureHandler } from "@/lib/capture/handlers/emailCaptureHandler";
import { peopleTaskCaptureHandler } from "@/lib/capture/handlers/peopleTaskCaptureHandler";
import { fileCaptureHandler } from "@/lib/capture/handlers/fileCaptureHandler";
import { calendarCaptureHandler } from "@/lib/capture/handlers/calendarCaptureHandler";
import { contactCaptureHandler } from "@/lib/capture/handlers/contactCaptureHandler";

export const MODULE_CAPTURE_HANDLERS: ReadonlyArray<ModuleCaptureHandler> = [
  checkinActionCaptureHandler as ModuleCaptureHandler,
  reminderCaptureHandler as ModuleCaptureHandler,
  reminderPreferencesCaptureHandler as ModuleCaptureHandler,
  agendaCaptureHandler as ModuleCaptureHandler,
  agendaActionCaptureHandler as ModuleCaptureHandler,
  createAgendaCaptureHandler as ModuleCaptureHandler,
  nutritionCaptureHandler as ModuleCaptureHandler,
  focusCaptureHandler as ModuleCaptureHandler,
  focusActionCaptureHandler as ModuleCaptureHandler,
  focusTimerCaptureHandler as ModuleCaptureHandler,
  sleepCaptureHandler as ModuleCaptureHandler,
  sleepActionCaptureHandler as ModuleCaptureHandler,
  // Habits carries the one product-specific twist: habit-extract returns a NAME,
  // and HabitConfirmModal resolves it to a habit_id (fuzzy-match or auto-create)
  // before completing. Each module is still one entry here
  //   (propose_<module>_log -> <module>-extract -> <Module>Modal -> table).
  habitCaptureHandler as ModuleCaptureHandler,
  habitActionCaptureHandler as ModuleCaptureHandler,
  createHabitCaptureHandler as ModuleCaptureHandler,
  // Workout: clone of the Focus mold. name (NOT NULL) + optional duration ->
  // workout-extract -> WorkoutLogModal -> workout_sessions (useWorkout writer).
  workoutCaptureHandler as ModuleCaptureHandler,
  // Journal: clone of the Focus/Sleep mold. content (NOT NULL) + mode (NOT NULL
  // + CHECK) + optional mood -> journal-extract -> JournalLogModal ->
  // journal_entries (useJournal writer, source 'voice').
  journalCaptureHandler as ModuleCaptureHandler,
  // Finance proposals and the legacy transaction-create alias share human
  // review, fresh full snapshots and the exact-operation receipt path.
  financeActionCaptureHandler as ModuleCaptureHandler,
  financeCaptureHandler as ModuleCaptureHandler,
  // Body/health drafts use the reviewed executor and durable receipts. Legacy
  // raw body phrases extract draft values only, then open the same review.
  bodyHealthActionCaptureHandler as ModuleCaptureHandler,
  bodyHealthLegacyCaptureHandler as ModuleCaptureHandler,
  // Email Executor: structured tool args -> responsive review/search modal ->
  // provider-neutral email-executor edge. Writes always require confirmation.
  emailCaptureHandler as ModuleCaptureHandler,
  calendarCaptureHandler as ModuleCaptureHandler,
  contactCaptureHandler as ModuleCaptureHandler,
  // Files & Knowledge Executor: connected Drive/OneDrive/Notion actions.
  // Search/read can pin explicit context; every external write is reviewed.
  fileCaptureHandler as ModuleCaptureHandler,
  connectedMessageCaptureHandler as ModuleCaptureHandler,
  connectedTaskCaptureHandler as ModuleCaptureHandler,
  peopleTaskCaptureHandler as ModuleCaptureHandler,
  personalActionCaptureHandler as ModuleCaptureHandler,
  goalActionCaptureHandler as ModuleCaptureHandler,
  goalCaptureHandler as ModuleCaptureHandler,
  taskActionCaptureHandler as ModuleCaptureHandler,
  legacyTaskCaptureHandler as ModuleCaptureHandler,
  noteActionCaptureHandler as ModuleCaptureHandler,
  legacyNoteCaptureHandler as ModuleCaptureHandler,
];
